(() => {
  const ELEMENT_NAME = 'personalised-bundle-builder';
  const STEPS = ['name', 'harness', 'leash', 'collar', 'toy', 'review'];
  const PRODUCT_STEPS = ['harness', 'leash', 'collar', 'toy'];
  const BUNDLE_STEPS = ['harness', 'name_patch', 'd_ring', 'leash', 'collar', 'toy'];
  const BUNDLE_OFFER = 'personalised_walk_pack_usd_99_v1';
  const BUNDLE_BASE_CURRENCY = 'USD';
  const BUNDLE_BASE_PRICE_CENTS = 9900;
  const BUNDLE_COMPARE_AT_PRICE_CENTS = 19400;
  const ZERO_DECIMAL_CURRENCIES = new Set(
    ['BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'],
  );
  const THREE_DECIMAL_CURRENCIES = new Set(['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND']);
  const SUPPORTED_FIAT_CURRENCIES = new Set(
    [
      'AED', 'AFN', 'ALL', 'AMD', 'ANG', 'AOA', 'ARS', 'AUD', 'AWG', 'AZN', 'BAM', 'BBD', 'BDT', 'BGN', 'BHD',
      'BIF', 'BMD', 'BND', 'BOB', 'BRL', 'BSD', 'BTN', 'BWP', 'BYN', 'BZD', 'CAD', 'CDF', 'CHF', 'CLP', 'CNY',
      'COP', 'CRC', 'CVE', 'CZK', 'DJF', 'DKK', 'DOP', 'DZD', 'EGP', 'ERN', 'ETB', 'EUR', 'FJD', 'FKP',
      'GBP', 'GEL', 'GHS', 'GIP', 'GMD', 'GNF', 'GTQ', 'GYD', 'HKD', 'HNL', 'HRK', 'HTG', 'HUF', 'IDR', 'ILS', 'INR',
      'IQD', 'IRR', 'ISK', 'JEP', 'JMD', 'JOD', 'JPY', 'KES', 'KGS', 'KHR', 'KID', 'KMF', 'KRW', 'KWD', 'KYD', 'KZT',
      'LAK', 'LBP', 'LKR', 'LRD', 'LSL', 'LTL', 'LVL', 'LYD', 'MAD', 'MDL', 'MGA', 'MKD', 'MMK', 'MNT', 'MOP', 'MRU', 'MUR',
      'MVR', 'MWK', 'MXN', 'MYR', 'MZN', 'NAD', 'NGN', 'NIO', 'NOK', 'NPR', 'NZD', 'OMR', 'PAB', 'PEN', 'PGK',
      'PHP', 'PKR', 'PLN', 'PYG', 'QAR', 'RON', 'RSD', 'RUB', 'RWF', 'SAR', 'SBD', 'SCR', 'SDG', 'SEK', 'SGD',
      'SHP', 'SLL', 'SOS', 'SRD', 'SSP', 'STN', 'SYP', 'SZL', 'THB', 'TJS', 'TMT', 'TND', 'TOP', 'TRY',
      'TTD', 'TWD', 'TZS', 'UAH', 'UGX', 'USD', 'UYU', 'UZS', 'VED', 'VES', 'VND', 'VUV', 'WST', 'XAF', 'XCD',
      'XOF', 'XPF', 'YER', 'ZAR', 'ZMW',
    ],
  );

  const currencyFractionDigits = (currencyCode) => {
    const code = String(currencyCode || '').trim().toUpperCase();
    if (!SUPPORTED_FIAT_CURRENCIES.has(code)) return null;
    if (ZERO_DECIMAL_CURRENCIES.has(code)) return 0;
    if (THREE_DECIMAL_CURRENCIES.has(code)) return 3;
    return 2;
  };

  const roundHalfUp = (value) => {
    if (!Number.isFinite(value) || value < 0) return null;
    return Math.floor(value + 0.5 + Number.EPSILON * Math.max(1, Math.abs(value)));
  };

  const formatPresentmentPrice = (amount, currencyCode, locale, fractionDigits, minorAmount) => {
    const minimumFractionDigits =
      currencyCode === BUNDLE_BASE_CURRENCY && minorAmount % (10 ** fractionDigits) === 0
        ? 0
        : fractionDigits;
    const options = {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits,
      maximumFractionDigits: fractionDigits,
    };

    try {
      return new Intl.NumberFormat(locale || undefined, options).format(amount);
    } catch (error) {
      return new Intl.NumberFormat('en', options).format(amount);
    }
  };

  const createPresentmentQuote = ({
    basePriceCents = BUNDLE_BASE_PRICE_CENTS,
    baseCurrencyCode = BUNDLE_BASE_CURRENCY,
    currencyCode,
    currencyRate,
    activeCurrencyCode,
    locale = 'en',
  } = {}) => {
    const baseCurrency = String(baseCurrencyCode || '').trim().toUpperCase();
    const currency = String(currencyCode || '').trim().toUpperCase();
    const activeCurrency = String(activeCurrencyCode || '').trim().toUpperCase();
    const priceCents = Number(basePriceCents);
    const rate = Number(currencyRate);
    const fractionDigits = currencyFractionDigits(currency);

    if (
      baseCurrency !== BUNDLE_BASE_CURRENCY ||
      priceCents !== BUNDLE_BASE_PRICE_CENTS ||
      fractionDigits === null ||
      !activeCurrency ||
      activeCurrency !== currency ||
      !Number.isFinite(rate) ||
      rate <= 0 ||
      (currency === BUNDLE_BASE_CURRENCY && rate !== 1)
    ) {
      return {
        ok: false,
        currencyCode: currency,
        message: 'We could not confirm the local bundle price. Refresh the page before checkout.',
      };
    }

    const isoMinorScale = 10 ** fractionDigits;
    const minorAmount = roundHalfUp((priceCents / 100) * rate * isoMinorScale);
    if (!Number.isSafeInteger(minorAmount) || minorAmount <= 0) {
      return {
        ok: false,
        currencyCode: currency,
        message: 'We could not confirm the local bundle price. Refresh the page before checkout.',
      };
    }

    // Shopify Ajax money values use two appended digits for zero-decimal currencies.
    const expectedCartAmount = fractionDigits === 0 ? minorAmount * 100 : minorAmount;
    const cartTolerance = currency === BUNDLE_BASE_CURRENCY ? 0 : fractionDigits === 0 ? 100 : 1;
    const amount = minorAmount / isoMinorScale;

    return {
      ok: true,
      baseCurrencyCode: baseCurrency,
      basePriceCents: priceCents,
      currencyCode: currency,
      currencyRate: rate,
      fractionDigits,
      minorAmount,
      expectedCartAmount,
      cartTolerance,
      formattedPrice: formatPresentmentPrice(amount, currency, locale, fractionDigits, minorAmount),
    };
  };

  const createPresentmentDisplayPrice = ({ basePriceCents, quote, locale = 'en' } = {}) => {
    const priceCents = Number(basePriceCents);
    const baseCurrency = String(quote?.baseCurrencyCode || '').trim().toUpperCase();
    const currency = String(quote?.currencyCode || '').trim().toUpperCase();
    const quotedBasePriceCents = Number(quote?.basePriceCents);
    const rate = Number(quote?.currencyRate);
    const fractionDigits = currencyFractionDigits(currency);
    const unavailable = { ok: false, currencyCode: currency, formattedPrice: '' };

    if (
      !quote?.ok ||
      baseCurrency !== BUNDLE_BASE_CURRENCY ||
      quotedBasePriceCents !== BUNDLE_BASE_PRICE_CENTS ||
      !Number.isSafeInteger(priceCents) ||
      priceCents <= 0 ||
      fractionDigits === null ||
      fractionDigits !== quote.fractionDigits ||
      !Number.isFinite(rate) ||
      rate <= 0 ||
      (currency === BUNDLE_BASE_CURRENCY && rate !== 1)
    ) {
      return unavailable;
    }

    const isoMinorScale = 10 ** fractionDigits;
    const minorAmount = roundHalfUp((priceCents / 100) * rate * isoMinorScale);
    if (!Number.isSafeInteger(minorAmount) || minorAmount <= 0) return unavailable;

    return {
      ok: true,
      currencyCode: currency,
      currencyRate: rate,
      fractionDigits,
      minorAmount,
      formattedPrice: formatPresentmentPrice(
        minorAmount / isoMinorScale,
        currency,
        locale,
        fractionDigits,
        minorAmount,
      ),
    };
  };

  const parseJson = (element, fallback = null) => {
    if (!element) return fallback;

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error('Personalised bundle builder JSON could not be parsed.', error);
      return fallback;
    }
  };

  const optionName = (product, index) => {
    const option = product?.options?.[index];
    return typeof option === 'string' ? option : option?.name || `Option ${index + 1}`;
  };

  const optionIndex = (product, expression) => {
    if (!Array.isArray(product?.options)) return -1;
    return product.options.findIndex((option, index) => expression.test(optionName(product, index)));
  };

  const availableVariants = (product) => (product?.variants || []).filter((variant) => variant.available);

  const normalise = (value) =>
    String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const displayVariantTitle = (value) => (normalise(value) === 'default title' ? '' : String(value || ''));

  const normaliseColour = (value) =>
    normalise(value)
      .replace(/\b(dog|harness|leash|lead|collar|tactical|essential|essentials|classic|bungee|rope|kit|bundle|personalized|personalised|custom|premium|name|patch|patches)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const colourScore = (left, right) => {
    const a = normaliseColour(left);
    const b = normaliseColour(right);
    if (!a || !b) return 0;
    if (a === b) return 10;
    if (a.includes(b) || b.includes(a)) return 7;

    const aliases = {
      charcoal: ['grey', 'gray'],
      grey: ['gray', 'charcoal'],
      gray: ['grey', 'charcoal'],
      navy: ['blue'],
      blue: ['navy'],
      burgundy: ['wine', 'maroon'],
      wine: ['burgundy', 'maroon'],
      pink: ['rose'],
      green: ['olive', 'khaki'],
      cream: ['beige', 'sand'],
    };

    const aTokens = a.split(' ');
    const bTokens = new Set(b.split(' '));
    let score = 0;

    aTokens.forEach((token) => {
      if (bTokens.has(token)) score = Math.max(score, 5);
      (aliases[token] || []).forEach((alias) => {
        if (bTokens.has(alias)) score = Math.max(score, 3);
      });
    });

    return score;
  };

  const normaliseSize = (value) =>
    normalise(value)
      .replace(/extra extra small/g, 'xxs')
      .replace(/extra small/g, 'xs')
      .replace(/extra large/g, 'xl')
      .replace(/\bsmall\b/g, 's')
      .replace(/\bmedium\b/g, 'm')
      .replace(/\blarge\b/g, 'l')
      .replace(/\b(size|patch|harness|collar|attachment)\b/g, ' ')
      .replace(/[^a-z0-9]+/g, '')
      .trim();

  const harnessSizeToken = (value) => {
    const size = normaliseSize(value);
    return ['xxs', 'xs', 's', 'm', 'l', 'xl'].find((token) => size === token) || '';
  };

  const companionRangeForHarness = (value) => {
    const size = harnessSizeToken(value);
    if (size === 'xxs' || size === 'xs') return 'xxsxs';
    if (['s', 'm', 'l', 'xl'].includes(size)) return 'sxl';
    return '';
  };

  const companionSizeScore = (harnessSize, candidateSize) => {
    const targetRange = companionRangeForHarness(harnessSize);
    const candidate = normaliseSize(candidateSize);
    if (!targetRange || !candidate) return -1;
    if (candidate === targetRange) return 100;
    return -1;
  };

  const exactCompanionVariant = (product, harness) => {
    const variants = availableVariants(product);
    if (!harness || !variants.length) return null;

    const sizeIndex = optionIndex(product, /size/i);
    const colourIndex = optionIndex(product, /colou?r/i);
    const harnessColour = normaliseColour(harness.colour);

    let best = null;
    let bestScore = -1;
    variants.forEach((variant) => {
      const candidateSize = sizeIndex >= 0 ? variant.options?.[sizeIndex] : variant.title;
      const sizeMatch = companionSizeScore(harness.size, candidateSize);
      if (sizeMatch < 0) return;
      if (colourIndex >= 0) {
        const candidateColour = normaliseColour(variant.options?.[colourIndex]);
        if (!harnessColour || candidateColour !== harnessColour) return;
      }
      const score = sizeMatch;
      if (score > bestScore) {
        best = variant;
        bestScore = score;
      }
    });

    return best;
  };

  const itemGroup = (item) => String(item?.properties?._dfc_bundle_group || '');

  const groupLines = (cart, bundleId) =>
    (cart?.items || []).filter((item) => itemGroup(item) === String(bundleId));

  const isPersonalisedBundleLine = (item) => {
    const properties = item?.properties || {};
    return (
      Boolean(itemGroup(item)) &&
      String(properties._dfc_personalised_bundle || '') === 'v1' &&
      String(properties._dfc_bundle_offer || '') === BUNDLE_OFFER
    );
  };

  const otherPersonalisedBundleLines = (cart, bundleId) =>
    (cart?.items || []).filter(
      (item) => isPersonalisedBundleLine(item) && itemGroup(item) !== String(bundleId),
    );

  const reconcileNamePatchAttributes = (items = [], existingAttributes = {}) => {
    const updates = {};
    Object.keys(existingAttributes || {}).forEach((key) => {
      if (/^namepatchtext-\d+-PATCH-\d+$/.test(key)) updates[key] = '';
    });

    const byParent = new Map();
    items.forEach((item) => {
      const properties = item?.properties || {};
      if (String(properties._dfc_patch_included_with_mapped_bundle) !== 'true') return;
      const parentId = String(properties._dfc_mapped_bundle_parent_variant_id || '');
      const dogName = String(properties['Name Patch Text'] || '').trim();
      if (!/^\d+$/.test(parentId) || !dogName) return;
      if (!byParent.has(parentId)) byParent.set(parentId, []);
      byParent.get(parentId).push({ dogName, group: itemGroup(item) });
    });

    byParent.forEach((patches, parentId) => {
      patches
        .sort((left, right) => left.group.localeCompare(right.group))
        .forEach((patch, index) => {
          updates[`namepatchtext-${index + 1}-PATCH-${parentId}`] = patch.dogName;
        });
    });

    return updates;
  };

  const namePatchAttributesConfirmed = (cart) => {
    const expected = reconcileNamePatchAttributes(cart?.items, cart?.attributes);
    const actual = cart?.attributes || {};

    return Object.entries(expected).every(([key, value]) => {
      if (value === '') return !actual[key] || String(actual[key]) === '';
      return String(actual[key] || '') === String(value);
    });
  };

  const verifyBundleCart = (
    cart,
    bundleId,
    expectedPriceCents,
    enforcePrice = true,
    expectedCurrencyCode = BUNDLE_BASE_CURRENCY,
    priceTolerance = 0,
  ) => {
    const lines = groupLines(cart, bundleId);
    const steps = lines.map((line) => String(line?.properties?._dfc_bundle_step || '')).sort();
    const expectedSteps = [...BUNDLE_STEPS].sort();
    const exactSteps = steps.length === expectedSteps.length && steps.every((step, index) => step === expectedSteps[index]);
    const exactQuantities = lines.every((line) => Number(line.quantity) === 1);
    const total = lines.reduce(
      (sum, line) => sum + Number(line.final_line_price ?? line.line_price ?? 0),
      0,
    );
    const currency = String(cart?.currency || '').toUpperCase();
    const expectedCurrency = String(expectedCurrencyCode || '').toUpperCase();
    const expectedPrice = Number(expectedPriceCents);
    const tolerance = Number(priceTolerance);

    if (!exactSteps || !exactQuantities) {
      return {
        ok: false,
        lines,
        total,
        currency,
        message: 'The store did not return one valid item for every pack component.',
      };
    }
    if (!currency || currency !== expectedCurrency) {
      return {
        ok: false,
        lines,
        total,
        currency,
        message: 'The cart currency changed from ' + expectedCurrency + '. No bundle items were kept in the cart.',
      };
    }
    if (
      enforcePrice &&
      (
        !Number.isSafeInteger(total) ||
        !Number.isSafeInteger(expectedPrice) ||
        !Number.isSafeInteger(tolerance) ||
        tolerance < 0 ||
        Math.abs(total - expectedPrice) > tolerance
      )
    ) {
      return {
        ok: false,
        lines,
        total,
        currency,
        message: 'The store did not confirm the fixed bundle price, so the incomplete pack was removed.',
      };
    }
    return { ok: true, lines, total, currency, message: '' };
  };

  const createBundleId = (harnessVariantId, now = Date.now()) => {
    const randomUuid = globalThis.crypto?.randomUUID?.().replaceAll('-', '');
    const randomPart = randomUuid || Math.random().toString(36).slice(2, 18);
    return `pbb1-${String(harnessVariantId).replace(/[^0-9]/g, '')}-${Number(now).toString(36)}-${randomPart.slice(0, 16)}`;
  };

  const imageUrl = (product, variant, fallback = '') => {
    const source = variant?.featured_image?.src || variant?.featured_image || product?.featured_image || product?.images?.[0] || fallback;
    if (!source) return '';
    return String(source).startsWith('//') ? `https:${source}` : String(source);
  };

  const reviewImageUrl = (step, product, variant, namePatchReviewImage = '') => {
    if (step === 'name_patch' && namePatchReviewImage) {
      return imageUrl(null, null, namePatchReviewImage);
    }
    if (step === 'name_patch') return '';
    return imageUrl(product, variant);
  };

  const swatchColour = (value) => {
    const key = normaliseColour(value);
    const map = {
      charcoal: '#3e4144',
      grey: '#8b8d8f',
      gray: '#8b8d8f',
      navy: '#16284d',
      blue: '#1d5ca8',
      burgundy: '#76263c',
      black: '#111111',
      white: '#f7f7f7',
      cream: '#e9dfca',
      beige: '#d8c5a3',
      red: '#b91c1c',
      pink: '#e78baa',
      green: '#3f6e4b',
      olive: '#697044',
      orange: '#ea6a21',
      purple: '#6d3d91',
    };

    const exact = map[key];
    if (exact) return exact;
    const token = key.split(' ').find((part) => map[part]);
    return token ? map[token] : '#d7d7d7';
  };

  const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

  class PersonalisedBundleBuilder extends HTMLElementBase {
    connectedCallback() {
      if (this.dataset.pbbInitialised === 'true') return;
      this.dataset.pbbInitialised = 'true';

      this.config = parseJson(this.querySelector('[data-pbb-config]'), {});
      this.dRingProduct = parseJson(this.querySelector('[data-pbb-companion="d-ring"]'));
      this.state = { dogName: '', selections: {} };
      this.currentStep = 'name';
      this.maxReachedIndex = 0;
      this.reviewEdit = null;
      this.checkoutStarted = false;
      this.checkoutFallbackReady = false;
      this.sizeGuideDialog = this.querySelector('[data-pbb-size-guide-dialog]');
      this.sizeGuideReturnFocus = null;
      this.sizeGuideRestoreFocus = true;
      this.sizeGuideScrollLock = null;
      this.prepareSizeGuideContent();
      this.updatePresentmentPricing();

      this.panels = new Map(
        [...this.querySelectorAll('[data-pbb-panel]')].map((panel) => [panel.dataset.pbbPanel, panel]),
      );
      this.stepLinks = new Map(
        [...this.querySelectorAll('[data-pbb-step-link]')].map((button) => [button.dataset.pbbStepLink, button]),
      );
      this.cards = [...this.querySelectorAll('[data-pbb-card]')].map((card) => this.setupCard(card));

      this.bindEvents();
      this.showStep('name', { scroll: false });
      this.updateDogNameUi();
      this.updateProgress();
      document.fonts?.ready?.then(() => {
        if (this.isConnected) this.updateNamePreviews();
      });
    }

    disconnectedCallback() {
      this.sizeGuideRestoreFocus = false;
      if (this.sizeGuideDialog?.open && typeof this.sizeGuideDialog.close === 'function') {
        this.sizeGuideDialog.close();
      }
      this.finishSizeGuideClose({ restoreFocus: false });
    }

    copy(key, fallback) {
      const value = this.config?.copy?.[key];
      return typeof value === 'string' && value.trim() ? value : fallback;
    }

    updatePresentmentPricing() {
      const shopifyCurrency = window.Shopify?.currency || {};
      const quote = createPresentmentQuote({
        basePriceCents: this.config?.basePriceCents,
        baseCurrencyCode: this.config?.baseCurrencyCode,
        currencyCode: this.config?.currencyCode,
        currencyRate: shopifyCurrency.rate,
        activeCurrencyCode: shopifyCurrency.active,
        locale: this.config?.locale,
      });
      this.presentmentQuote = quote;
      const compareAtQuote = createPresentmentDisplayPrice({
        basePriceCents: BUNDLE_COMPARE_AT_PRICE_CENTS,
        quote,
        locale: this.config?.locale,
      });

      const priceText = quote.ok ? quote.formattedPrice : 'Unavailable';
      const isBaseCurrency = quote.ok && quote.currencyCode === BUNDLE_BASE_CURRENCY;
      const priceContext = quote.ok
        ? isBaseCurrency
          ? quote.currencyCode + ', fixed bundle price'
          : quote.currencyCode + ', equivalent to US$99'
        : 'Local price unavailable';
      const bundlePriceLabel = quote.ok
        ? isBaseCurrency
          ? 'Bundle price ' + quote.formattedPrice + ' in ' + quote.currencyCode
          : 'Bundle price ' + quote.formattedPrice + ' in ' + quote.currencyCode + ', equivalent to US$99'
        : 'Bundle price unavailable';
      const priceLabel = compareAtQuote.ok
        ? 'Regular price ' + compareAtQuote.formattedPrice + '. ' + bundlePriceLabel
        : bundlePriceLabel;

      this.querySelectorAll('[data-pbb-price]').forEach((element) => {
        element.textContent = priceText;
      });
      this.querySelectorAll('[data-pbb-compare-price]').forEach((element) => {
        element.textContent = compareAtQuote.ok ? compareAtQuote.formattedPrice : '';
        element.hidden = !compareAtQuote.ok;
        if (compareAtQuote.ok) {
          element.setAttribute('aria-label', 'Regular price ' + compareAtQuote.formattedPrice);
        } else {
          element.removeAttribute('aria-label');
        }
      });
      this.querySelectorAll('[data-pbb-price-context]').forEach((element) => {
        element.textContent = priceContext;
      });
      this.querySelectorAll('[data-pbb-price-label]').forEach((element) => {
        element.setAttribute('aria-label', priceLabel);
      });

      const verificationCopy = this.querySelector('[data-pbb-verification-copy]');
      if (verificationCopy) {
        verificationCopy.textContent = quote.ok
          ? isBaseCurrency
            ? 'We’ll verify the complete six-item pack totals ' + quote.formattedPrice + ' USD before checkout.'
            : 'We’ll verify the complete six-item pack totals ' + quote.formattedPrice + ' (' + quote.currencyCode + ', equivalent to US$99) before checkout.'
          : 'We’ll verify the complete six-item pack price before checkout.';
      }

      const pricingError = this.querySelector('[data-pbb-pricing-error]');
      if (pricingError) {
        pricingError.textContent = quote.ok ? '' : quote.message;
        pricingError.hidden = quote.ok;
      }

      const checkoutButton = this.querySelector('[data-pbb-add-bundle]');
      if (checkoutButton) {
        const permanentlyDisabled = checkoutButton.hasAttribute('data-pbb-cart-disabled');
        if (quote.ok) {
          checkoutButton.removeAttribute('data-pbb-pricing-disabled');
          if (!permanentlyDisabled && checkoutButton.title === 'Local bundle pricing could not be verified') {
            checkoutButton.title = '';
          }
        } else {
          checkoutButton.setAttribute('data-pbb-pricing-disabled', '');
          checkoutButton.disabled = true;
          if (!permanentlyDisabled) checkoutButton.title = 'Local bundle pricing could not be verified';
        }
      }

      return quote;
    }

    setupCard(card) {
      card.product = parseJson(card.querySelector('[data-pbb-product-json]'), {});
      card.displayTitle = String(card.dataset.pbbDisplayTitle || card.product.title || '');
      card.mappedPatchProduct = parseJson(card.querySelector('[data-pbb-mapped-patch-json]'));
      card.builderImage = String(card.dataset.pbbBuilderImage || '');
      card.variantSelect = card.querySelector('[data-pbb-variant-select]');
      card.image = card.querySelector('.pbb-card__image');
      card.namePreview = card.querySelector('[data-pbb-name-preview]');
      card.selectionLabel = card.querySelector('[data-pbb-card-selection]');
      card.optionControls = card.querySelector('[data-pbb-option-controls]');
      card.selectedVariant = this.variantById(card, card.variantSelect?.value) || availableVariants(card.product)[0];

      if (card.image && card.namePreview) {
        card.image.addEventListener('load', () => this.updateNamePreviews());
        card.image.addEventListener('error', () => this.updateNamePreviews());
      }

      if (!card.selectedVariant) return card;

      this.buildOptionControls(card);
      this.setVariant(card, card.selectedVariant, { syncSelection: false });
      card.classList.add('is-enhanced');
      return card;
    }

    bindEvents() {
      this.querySelector('[data-pbb-name-form]')?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitName();
      });

      this.addEventListener('click', (event) => {
        const sizeGuideTrigger = event.target.closest('[data-pbb-size-guide-trigger]');
        if (sizeGuideTrigger && this.contains(sizeGuideTrigger)) {
          if (this.shouldEnhanceSizeGuideClick(event) && this.openSizeGuide(sizeGuideTrigger)) {
            event.preventDefault();
          }
          return;
        }

        const optionButton = event.target.closest('[data-pbb-option-value]');
        if (optionButton && this.contains(optionButton)) {
          this.chooseOption(optionButton);
          return;
        }

        const chooseButton = event.target.closest('[data-pbb-choose]');
        if (chooseButton && this.contains(chooseButton)) {
          this.chooseCard(chooseButton.closest('[data-pbb-card]'));
          return;
        }

        const stepLink = event.target.closest('[data-pbb-step-link]');
        if (stepLink && !stepLink.disabled && this.contains(stepLink)) {
          const targetStep = stepLink.dataset.pbbStepLink;
          if (targetStep === this.currentStep) return;
          if (this.reviewEdit) this.cancelReviewEdit({ showReview: false });
          else this.restoreSavedSelection(this.currentStep);
          this.showStep(targetStep);
          return;
        }

        const changeButton = event.target.closest('[data-pbb-change-step]');
        if (changeButton && this.contains(changeButton)) {
          this.beginReviewEdit(changeButton.dataset.pbbChangeStep);
          return;
        }

        if (event.target.closest('[data-pbb-back]')) {
          this.goBack();
          return;
        }

        if (event.target.closest('[data-pbb-add-bundle]')) {
          if (this.checkoutFallbackReady) {
            window.location.assign(this.config.cartUrl || '/cart');
            return;
          }
          this.addBundleToCart();
        }
      });

      this.addEventListener('change', (event) => {
        const optionSelect = event.target.closest('[data-pbb-option-select]');
        if (optionSelect && this.contains(optionSelect)) {
          const card = optionSelect.closest('[data-pbb-card]');
          this.chooseOptionValue(card, Number(optionSelect.dataset.pbbOptionIndex), optionSelect.value);
          return;
        }

        const select = event.target.closest('[data-pbb-variant-select]');
        if (!select || !this.contains(select)) return;
        const card = select.closest('[data-pbb-card]');
        const variant = this.variantById(card, select.value);
        if (variant) this.setVariant(card, variant);
      });

      this.sizeGuideDialog?.addEventListener('click', (event) => {
        const closeButton = event.target.closest('[data-pbb-size-guide-close]');
        if (closeButton && this.sizeGuideDialog.contains(closeButton)) {
          this.closeSizeGuide();
          return;
        }
        if (event.target === this.sizeGuideDialog) this.closeSizeGuide();
      });
      this.sizeGuideDialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeSizeGuide();
      });
      this.sizeGuideDialog?.addEventListener('close', () => {
        this.finishSizeGuideClose();
      });
    }

    shouldEnhanceSizeGuideClick(event) {
      return (
        !event.defaultPrevented &&
        event.button === 0 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey
      );
    }

    prepareSizeGuideContent() {
      if (!this.sizeGuideDialog) return;

      this.sizeGuideDialog.querySelectorAll('.pbb__size-guide-page').forEach((page, pageIndex) => {
        page.querySelectorAll('meta').forEach((meta) => meta.remove());
        page.querySelectorAll('.chart-title').forEach((title) => title.remove());

        const idMap = new Map();
        page.querySelectorAll('[id]').forEach((element, elementIndex) => {
          const sourceId = String(element.id || '').trim();
          if (!sourceId) return;
          const safeId = `PbbSizeGuideSource-${this.dataset.sectionId || 'section'}-${pageIndex}-${elementIndex}`;
          if (!idMap.has(sourceId)) idMap.set(sourceId, safeId);
          element.dataset.pbbSourceId = sourceId;
          element.id = safeId;
        });

        const tokenReferenceAttributes = ['for', 'aria-labelledby', 'aria-describedby', 'aria-controls', 'headers'];
        tokenReferenceAttributes.forEach((attribute) => {
          page.querySelectorAll(`[${attribute}]`).forEach((element) => {
            const nextValue = String(element.getAttribute(attribute) || '')
              .split(/\s+/)
              .filter(Boolean)
              .map((token) => idMap.get(token) || token)
              .join(' ');
            if (nextValue) element.setAttribute(attribute, nextValue);
          });
        });

        page.querySelectorAll('a[href^="#"]').forEach((anchor) => {
          const sourceId = String(anchor.getAttribute('href') || '').slice(1);
          if (idMap.has(sourceId)) anchor.setAttribute('href', `#${idMap.get(sourceId)}`);
        });
      });
    }

    openSizeGuide(trigger) {
      const dialog = this.sizeGuideDialog;
      if (!dialog || typeof dialog.showModal !== 'function') return false;

      const guideKey = String(trigger?.dataset.pbbSizeGuideTrigger || '');
      const panels = [...dialog.querySelectorAll('[data-pbb-size-guide-panel]')];
      const activePanel = panels.find((panel) => panel.dataset.pbbSizeGuidePanel === guideKey);
      if (!activePanel) return false;

      panels.forEach((panel) => {
        panel.hidden = panel !== activePanel;
      });
      this.sizeGuideReturnFocus = trigger;
      this.sizeGuideRestoreFocus = true;

      try {
        if (!dialog.open) dialog.showModal();
      } catch (error) {
        this.sizeGuideReturnFocus = null;
        return false;
      }

      this.lockSizeGuideScroll();
      dialog.querySelector('[data-pbb-size-guide-dialog-title]')?.focus({ preventScroll: true });
      return true;
    }

    closeSizeGuide({ restoreFocus = true } = {}) {
      this.sizeGuideRestoreFocus = restoreFocus;
      if (this.sizeGuideDialog?.open && typeof this.sizeGuideDialog.close === 'function') {
        this.sizeGuideDialog.close();
        return;
      }
      this.finishSizeGuideClose({ restoreFocus });
    }

    lockSizeGuideScroll() {
      if (this.sizeGuideScrollLock || !document.body) return;
      const body = document.body;
      const scrollY = window.scrollY || window.pageYOffset || 0;
      this.sizeGuideScrollLock = {
        body,
        scrollY,
        position: body.style.position,
        top: body.style.top,
        right: body.style.right,
        left: body.style.left,
        width: body.style.width,
        overflow: body.style.overflow,
      };
      body.classList.add('pbb-size-guide-open');
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.right = '0';
      body.style.left = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
    }

    finishSizeGuideClose({ restoreFocus = this.sizeGuideRestoreFocus } = {}) {
      const lock = this.sizeGuideScrollLock;
      if (lock) {
        const { body, scrollY } = lock;
        body.classList.remove('pbb-size-guide-open');
        body.style.position = lock.position;
        body.style.top = lock.top;
        body.style.right = lock.right;
        body.style.left = lock.left;
        body.style.width = lock.width;
        body.style.overflow = lock.overflow;
        this.sizeGuideScrollLock = null;
        window.scrollTo(0, scrollY);
      }

      const returnFocus = this.sizeGuideReturnFocus;
      this.sizeGuideReturnFocus = null;
      this.sizeGuideRestoreFocus = true;
      if (restoreFocus && returnFocus?.isConnected) {
        returnFocus.focus({ preventScroll: true });
      }
    }

    buildOptionControls(card) {
      const product = card.product;
      const variants = availableVariants(product);
      if (!card.optionControls || !variants.length) return;

      card.optionControls.replaceChildren();
      (product.options || []).forEach((option, index) => {
        const name = optionName(product, index);
        const values = [...new Set(variants.map((variant) => variant.options?.[index]).filter(Boolean))];
        if (!values.length || (values.length === 1 && normalise(values[0]) === 'default title')) return;

        const fieldset = document.createElement('fieldset');
        fieldset.className = 'pbb-option';
        fieldset.dataset.pbbOptionIndex = String(index);

        const isColour = /colou?r/i.test(name);
        const isSize = /size/i.test(name);
        if (isSize) {
          fieldset.dataset.pbbSizeOption = '';
          card.classList.add('has-size-option');
        }
        const legend = document.createElement('legend');
        legend.textContent = isColour ? this.copy('colorLabel', 'Color') : name;
        fieldset.append(legend);

        const valueGroup = document.createElement('div');
        valueGroup.className = 'pbb-option__values';

        values.forEach((value) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = `pbb-option__value${isColour ? ' is-colour' : ''}`;
          button.dataset.pbbOptionValue = value;
          button.dataset.pbbOptionIndex = String(index);
          button.setAttribute('aria-pressed', 'false');
          button.textContent = value;
          if (isColour) button.style.setProperty('--pbb-swatch', swatchColour(value));
          valueGroup.append(button);
        });

        fieldset.append(valueGroup);
        if (isSize) {
          const select = document.createElement('select');
          select.className = 'pbb-option__select';
          select.dataset.pbbOptionSelect = '';
          select.dataset.pbbOptionIndex = String(index);
          select.setAttribute('aria-label', name);
          values.forEach((value) => {
            const optionElement = document.createElement('option');
            optionElement.value = value;
            optionElement.textContent = value;
            select.append(optionElement);
          });
          fieldset.append(select);
        }
        card.optionControls.append(fieldset);
      });
    }

    chooseOption(button) {
      const card = button.closest('[data-pbb-card]');
      const index = Number(button.dataset.pbbOptionIndex);
      const value = button.dataset.pbbOptionValue;
      this.chooseOptionValue(card, index, value);
    }

    chooseOptionValue(card, index, value) {
      if (!card || !Number.isInteger(index) || !value) return;
      const currentOptions = [...(card.selectedVariant?.options || [])];
      currentOptions[index] = value;

      let variant = availableVariants(card.product).find((candidate) =>
        candidate.options?.every((optionValue, optionIndexValue) => optionValue === currentOptions[optionIndexValue]),
      );

      if (!variant) {
        variant = availableVariants(card.product).find((candidate) => candidate.options?.[index] === value);
      }

      if (variant) this.setVariant(card, variant);
    }

    setVariant(card, variant, { syncSelection = true } = {}) {
      card.selectedVariant = variant;
      if (card.variantSelect) card.variantSelect.value = String(variant.id);
      if (card.selectionLabel) card.selectionLabel.textContent = displayVariantTitle(variant.title);

      card.querySelectorAll('[data-pbb-option-value]').forEach((button) => {
        const index = Number(button.dataset.pbbOptionIndex);
        button.setAttribute('aria-pressed', String(variant.options?.[index] === button.dataset.pbbOptionValue));
      });

      card.querySelectorAll('[data-pbb-option-select]').forEach((select) => {
        const index = Number(select.dataset.pbbOptionIndex);
        select.value = variant.options?.[index] || '';
      });

      const nextImage = card.builderImage || imageUrl(card.product, variant, card.image?.src);
      if (card.image && nextImage) {
        card.image.src = nextImage;
        card.image.removeAttribute('srcset');
      }
      this.updateNamePreviews();

      const chooseButton = card.querySelector('[data-pbb-choose]');
      const includedComponentsAvailable =
        card.dataset.step !== 'harness' || this.harnessCompanionsAvailable(card, variant);
      if (card.dataset.step === 'harness') {
        card.classList.toggle('is-bundle-unavailable', !includedComponentsAvailable);
      }
      if (chooseButton) {
        chooseButton.disabled = !variant.available || !includedComponentsAvailable;
        chooseButton.title = includedComponentsAvailable ? '' : 'Included patch or D-ring unavailable for this size';
      }
      if (syncSelection || card.classList.contains('is-selected')) this.updateCardButton(card);
    }

    harnessCompanionsAvailable(card, variant = card?.selectedVariant) {
      if (!card || !variant) return false;
      const sizeIndex = optionIndex(card.product, /size/i);
      const colourIndex = optionIndex(card.product, /colou?r/i);
      const harnessSelection = {
        size: sizeIndex >= 0 ? variant.options?.[sizeIndex] : '',
        colour: colourIndex >= 0 ? variant.options?.[colourIndex] : card.dataset.color,
      };
      return Boolean(
        exactCompanionVariant(card.mappedPatchProduct, harnessSelection) &&
          exactCompanionVariant(this.dRingProduct, harnessSelection),
      );
    }

    variantById(card, id) {
      return card?.product?.variants?.find((variant) => String(variant.id) === String(id));
    }

    submitName() {
      const input = this.querySelector('[data-pbb-name-input]');
      const error = this.querySelector('[data-pbb-name-error]');
      const value = String(input?.value || '').trim().replace(/\s+/g, ' ');
      const valid = value.length > 0 && /^[A-Za-z0-9 ]+$/.test(value) && input?.checkValidity();

      if (!valid) {
        error.hidden = false;
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        return;
      }

      error.hidden = true;
      input.removeAttribute('aria-invalid');
      input.value = value;
      this.state.dogName = value;
      this.maxReachedIndex = Math.max(this.maxReachedIndex, 1);
      this.updateDogNameUi();
      this.unlockSteps();
      this.showStep('harness');
      this.announce(`Let's build ${value}'s ${this.copy('personalizedPack', 'personalized pack')}.`);
    }

    chooseCard(card) {
      if (!card?.selectedVariant?.available) return;
      const step = card.dataset.step;
      if (step === 'harness' && !this.harnessCompanionsAvailable(card)) {
        this.announce('This harness option does not have an available name patch and D-ring. Choose another size.');
        return;
      }
      this.cards
        .filter((candidate) => candidate.dataset.step === step)
        .forEach((candidate) => {
          const selected = candidate === card;
          candidate.classList.toggle('is-selected', selected);
          candidate.querySelector('[data-pbb-choose]')?.setAttribute('aria-pressed', String(selected));
        });

      const editingReview = this.reviewEdit?.step === step;
      const clearedSteps = this.saveSelection(card, { announce: !editingReview });
      this.updateCardButtons(step);
      this.updateProgress();

      const nextIndex = Math.min(STEPS.indexOf(step) + 1, STEPS.length - 1);
      this.maxReachedIndex = Math.max(this.maxReachedIndex, nextIndex);
      this.unlockSteps();
      if (editingReview) {
        this.reviewEdit = null;
        this.updateBackButton();
        this.showStep('review');
        const repairMessage = clearedSteps.length
          ? ` ${clearedSteps.join(' and ')} now needs a new selection.`
          : '';
        this.announce(`${this.state.dogName}'s ${step} was updated. Back at your pack review.${repairMessage}`);
        return;
      }
      this.showStep(STEPS[nextIndex]);
    }

    saveSelection(card, { announce = true } = {}) {
      const step = card.dataset.step;
      const variant = card.selectedVariant;
      const product = card.product;
      const colourIndex = optionIndex(product, /colou?r/i);
      const sizeIndex = optionIndex(product, /size/i);
      const fallbackImage = card.image?.src || '';

      this.state.selections[step] = {
        step,
        product,
        variant,
        variantId: variant.id,
        title: card.displayTitle || product.title,
        variantTitle: displayVariantTitle(variant.title),
        colour: colourIndex >= 0 ? variant.options?.[colourIndex] : card.dataset.color,
        size: sizeIndex >= 0 ? variant.options?.[sizeIndex] : '',
        image: card.builderImage || imageUrl(product, variant, fallbackImage),
        patchProduct: card.mappedPatchProduct,
        nameProfile: card.dataset.pbbNameProfile || '',
        nameTone: card.dataset.pbbNameTone || '',
        nameImageSignature: card.dataset.pbbNameImageSignature || '',
        card,
      };

      const clearedSteps = [];
      if (step === 'harness') {
        this.prepareColourMatches('leash');
        this.prepareColourMatches('collar');
        ['leash', 'collar', 'toy'].forEach((retainedStep) => {
          if (this.validateRetainedSelection(retainedStep)) clearedSteps.push(retainedStep);
        });
      }

      if (announce) {
        const choice = [card.displayTitle || product.title, displayVariantTitle(variant.title)].filter(Boolean).join(', ');
        this.announce(`${choice}, chosen for ${this.state.dogName}.`);
      }
      return clearedSteps;
    }

    validateRetainedSelection(step) {
      const selection = this.state.selections[step];
      if (!selection) return false;
      const variant = this.variantById(selection.card, selection.variantId);
      if (variant?.available) return false;

      selection.card?.classList.remove('is-selected', 'is-draft');
      delete this.state.selections[step];
      this.updateCardButtons(step);
      return true;
    }

    prepareColourMatches(step) {
      const harness = this.state.selections.harness;
      const grid = this.querySelector(`[data-pbb-grid="${step}"]`);
      if (!harness || !grid) return;

      const stepCards = this.cards.filter((card) => card.dataset.step === step);
      stepCards.forEach((card, sourceIndex) => {
        const colourIndex = optionIndex(card.product, /colou?r/i);
        let matchScore = colourScore(card.dataset.color, harness.colour);

        if (colourIndex >= 0) {
          let bestVariant = null;
          let bestScore = -1;
          availableVariants(card.product).forEach((variant) => {
            const score = colourScore(variant.options?.[colourIndex], harness.colour);
            if (score > bestScore) {
              bestScore = score;
              bestVariant = variant;
            }
          });
          const preserveSelection = card.classList.contains('is-selected');
          if (!preserveSelection && bestVariant) this.setVariant(card, bestVariant, { syncSelection: false });
          matchScore = colourScore(card.selectedVariant?.options?.[colourIndex], harness.colour);
        }

        card.dataset.pbbMatchScore = String(matchScore);
        card.dataset.pbbSourceIndex = String(sourceIndex);
        const isMatch = matchScore >= 3;
        card.classList.toggle('is-colour-match', isMatch);
        const badge = card.querySelector('[data-pbb-match-badge]');
        if (badge) badge.hidden = !isMatch;
      });

      stepCards
        .sort((left, right) => {
          const scoreDelta = Number(right.dataset.pbbMatchScore) - Number(left.dataset.pbbMatchScore);
          return scoreDelta || Number(left.dataset.pbbSourceIndex) - Number(right.dataset.pbbSourceIndex);
        })
        .forEach((card) => grid.append(card));
    }

    beginReviewEdit(step) {
      const targetStep = PRODUCT_STEPS.includes(step) ? step : 'harness';
      const selection = this.state.selections[targetStep];
      const stepCards = this.cards.filter((card) => card.dataset.step === targetStep);
      if (!selection && !stepCards.length) return;

      this.reviewEdit = {
        step: targetStep,
        card: selection?.card || null,
        variantId: selection?.variantId || null,
        cardVariants: stepCards.map((card) => ({ card, variantId: card.selectedVariant?.id })),
      };
      this.updateBackButton();
      this.showStep(targetStep);
      this.announce(`Editing ${targetStep}. Choose an option to return directly to your pack review.`);
    }

    cancelReviewEdit({ showReview = true } = {}) {
      if (!this.reviewEdit) return false;
      const { step, cardVariants = [] } = this.reviewEdit;
      this.reviewEdit = null;

      cardVariants.forEach(({ card, variantId }) => {
        const savedVariant = this.variantById(card, variantId);
        if (card && savedVariant) this.setVariant(card, savedVariant, { syncSelection: false });
      });
      this.updateCardButtons(step);
      this.updateBackButton();
      if (showReview) this.showStep('review');
      return true;
    }

    restoreSavedSelection(step) {
      const selection = this.state.selections[step];
      if (!selection?.card) return;
      const savedVariant = this.variantById(selection.card, selection.variantId);
      if (savedVariant && String(selection.card.selectedVariant?.id) !== String(savedVariant.id)) {
        this.setVariant(selection.card, savedVariant, { syncSelection: false });
      }
      this.updateCardButtons(step);
    }

    showStep(step, { scroll = true } = {}) {
      if (!STEPS.includes(step) || STEPS.indexOf(step) > this.maxReachedIndex) return;
      if (step === 'leash' || step === 'collar') this.prepareColourMatches(step);

      this.currentStep = step;
      this.dataset.pbbCurrentStep = step;
      this.panels.forEach((panel, key) => {
        const active = key === step;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      if (step === 'review') this.renderReview();

      this.stepLinks.forEach((button, key) => {
        if (key === step) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });

      this.updateBackButton();
      this.keepProgressItemVisible(step);
      window.requestAnimationFrame(() => this.updateNamePreviews());

      if (scroll) {
        const panel = this.panels.get(step);
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const compactViewport = window.matchMedia?.('(max-width: 599px)').matches;
        window.requestAnimationFrame(() => {
          panel?.scrollIntoView({ behavior: reduceMotion || compactViewport ? 'auto' : 'smooth', block: 'start' });
          panel?.querySelector('[data-pbb-panel-heading]')?.focus({ preventScroll: true });
        });
      }
    }

    keepProgressItemVisible(step) {
      window.requestAnimationFrame(() => {
        const nav = this.querySelector('.pbb__step-nav');
        const stepItem = this.stepLinks.get(step)?.closest('li');
        this.centerScrollableItem(nav, stepItem);

        const stickyList = this.querySelector('.pbb__sticky-items');
        const stickyItem = this.querySelector(`[data-pbb-progress-item="${step}"]`);
        this.centerScrollableItem(stickyList, stickyItem);
      });
    }

    centerScrollableItem(container, item) {
      if (!container || !item || container.scrollWidth <= container.clientWidth) return;
      const left = item.offsetLeft - (container.clientWidth - item.offsetWidth) / 2;
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      container.scrollTo({ left: Math.max(0, left), behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    updateBackButton() {
      const backButton = this.querySelector('[data-pbb-back]');
      if (!backButton) return;
      backButton.hidden = this.currentStep === 'name';
      backButton.textContent = this.reviewEdit ? 'Cancel edit' : 'Back';
      backButton.setAttribute('aria-label', this.reviewEdit ? 'Cancel edit and return to review' : 'Go back one step');
    }

    goBack() {
      if (this.cancelReviewEdit()) return;
      this.restoreSavedSelection(this.currentStep);
      const index = STEPS.indexOf(this.currentStep);
      if (index > 0) this.showStep(STEPS[index - 1]);
    }

    unlockSteps() {
      this.stepLinks.forEach((button, key) => {
        const index = STEPS.indexOf(key);
        button.disabled = index > this.maxReachedIndex;
        const complete = key === 'name'
          ? Boolean(this.state.dogName)
          : PRODUCT_STEPS.includes(key)
            ? Boolean(this.state.selections[key]?.variantId)
            : key === 'review' && this.allSelectionsReady();
        button.classList.toggle('is-complete', complete);
      });
    }

    updateDogNameUi() {
      const name = this.state.dogName || 'your dog';
      this.querySelectorAll('[data-pbb-dog-name], [data-pbb-card-dog]').forEach((element) => {
        element.textContent = name;
      });
      this.updateNamePreviews();
      this.updateCardButtons();
    }

    updateNamePreviews() {
      const name = this.state.dogName;
      this.querySelectorAll('[data-pbb-name-preview]').forEach((element) => {
        const image = element.parentElement?.querySelector('img');
        const imageReady = Boolean(image?.complete && image.naturalWidth > 0);
        const previewHost = element.closest('[data-pbb-name-profile]');
        const expectedImageSignature = previewHost?.dataset.pbbNameImageSignature || '';
        const imageSource = String(image?.currentSrc || image?.src || image?.getAttribute('src') || '');
        const imageMatchesProfile = Boolean(expectedImageSignature) && imageSource.includes(expectedImageSignature);
        element.textContent = name;
        element.classList.toggle('is-long-name', name.length > 8);
        element.style.removeProperty('font-size');
        element.hidden = !name || !imageReady || !imageMatchesProfile;
        if (!element.hidden && element.scrollWidth > element.clientWidth) {
          const currentSize = Number.parseFloat(window.getComputedStyle(element).fontSize);
          const fittedSize = currentSize * (element.clientWidth / element.scrollWidth) * 0.98;
          element.style.fontSize = `${Math.max(3, fittedSize).toFixed(2)}px`;
        }
      });
      this.querySelectorAll('[data-pbb-review-patch-name]').forEach((element) => {
        element.textContent = name;
        element.classList.toggle('is-long-name', name.length > 8);
        element.style.removeProperty('font-size');
        element.hidden = !name;
        if (!element.hidden && element.scrollWidth > element.clientWidth) {
          const currentSize = Number.parseFloat(window.getComputedStyle(element).fontSize);
          const fittedSize = currentSize * (element.clientWidth / element.scrollWidth) * 0.96;
          element.style.fontSize = `${Math.max(5, fittedSize).toFixed(2)}px`;
        }
      });
    }

    updateCardButtons(step = null) {
      this.cards
        .filter((card) => !step || card.dataset.step === step)
        .forEach((card) => this.updateCardButton(card));
    }

    updateCardButton(card) {
      const button = card?.querySelector('[data-pbb-choose]');
      if (!button) return;

      const selection = this.state.selections[card.dataset.step];
      const selectedCard = selection?.card === card;
      const selectedVariant = selectedCard && String(selection.variantId) === String(card.selectedVariant?.id);
      const draftVariant = selectedCard && !selectedVariant;
      const chooseLabel = button.querySelector('[data-pbb-card-choose-label]');
      const selectedLabel = button.querySelector('[data-pbb-card-selected-label]');
      const updateLabel = button.querySelector('[data-pbb-card-update-label]');

      if (chooseLabel) chooseLabel.hidden = selectedVariant || draftVariant;
      if (selectedLabel) selectedLabel.hidden = !selectedVariant;
      if (updateLabel) updateLabel.hidden = !draftVariant;
      button.classList.toggle('is-in-pack', selectedVariant);
      button.classList.toggle('is-update', draftVariant);
      button.setAttribute('aria-pressed', String(selectedVariant));
      card.classList.toggle('is-draft', draftVariant);

      const dogName = this.state.dogName || 'your dog';
      if (selectedVariant) {
        button.setAttribute('aria-label', `This is in your pack already: ${card.displayTitle || card.product.title}. Continue with this selection`);
      } else if (draftVariant) {
        button.setAttribute('aria-label', `Update this selection: ${card.displayTitle || card.product.title} for ${dogName}`);
      } else {
        button.setAttribute('aria-label', `Choose for ${dogName}: ${card.displayTitle || card.product.title}`);
      }
    }

    updateProgress() {
      PRODUCT_STEPS.forEach((step) => {
        const item = this.querySelector(`[data-pbb-progress-item="${step}"]`);
        if (!item) return;
        const selection = this.state.selections[step];
        const label = item.querySelector('[data-pbb-progress-label]');
        item.classList.toggle('is-complete', Boolean(selection));
        label.textContent = selection ? selection.title : 'Not chosen';
      });
    }

    companionSelection(product, step) {
      if (!product) return null;
      const harness = this.state.selections.harness;
      const best = exactCompanionVariant(product, harness);
      if (!harness || !best) return null;
      const colourIndex = optionIndex(product, /colou?r/i);
      const sizeIndex = optionIndex(product, /size/i);

      return {
        step,
        product,
        variant: best,
        variantId: best.id,
        title: product.title,
        variantTitle: best.title,
        colour: colourIndex >= 0 ? best.options?.[colourIndex] : harness.colour,
        size: sizeIndex >= 0 ? best.options?.[sizeIndex] : harness.size,
        image: reviewImageUrl(step, product, best, this.config.namePatchReviewImage),
        nameTone: step === 'name_patch' ? harness.nameTone : '',
      };
    }

    renderReview() {
      const grid = this.querySelector('[data-pbb-review-grid]');
      if (!grid) return;
      grid.replaceChildren();

      const patchProduct = this.state.selections.harness?.patchProduct;
      const patch = this.companionSelection(patchProduct, 'name_patch');
      const dRing = this.companionSelection(this.dRingProduct, 'd_ring');
      const reviewItems = [
        this.state.selections.harness,
        patch,
        dRing,
        this.state.selections.leash,
        this.state.selections.collar,
        this.state.selections.toy,
      ];

      reviewItems.forEach((selection, index) => {
        grid.append(selection ? this.reviewItem(selection) : this.reviewMissingItem(BUNDLE_STEPS[index]));
      });
      const checkoutButton = this.querySelector('[data-pbb-add-bundle]');
      if (checkoutButton && !checkoutButton.hasAttribute('aria-busy')) {
        const permanentlyDisabled = checkoutButton.hasAttribute('data-pbb-cart-disabled');
        const pricingDisabled = checkoutButton.hasAttribute('data-pbb-pricing-disabled');
        checkoutButton.disabled = permanentlyDisabled || pricingDisabled || !this.allSelectionsReady();
        if (!permanentlyDisabled) {
          checkoutButton.title = pricingDisabled
            ? 'Local bundle pricing could not be verified'
            : checkoutButton.disabled
              ? 'Complete every pack selection before checkout'
              : '';
        }
      }
      this.updateNamePreviews();
    }

    reviewItem(selection) {
      const item = document.createElement('article');
      item.className = 'pbb-review-item';

      const media = document.createElement('div');
      media.className = 'pbb-review-item__media';

      const premiumPatchPreview = selection.step === 'name_patch' && selection.nameTone && selection.nameTone !== 'black';

      if (premiumPatchPreview) {
        media.dataset.pbbNameTone = selection.nameTone;
        const patchPair = document.createElement('div');
        patchPair.className = 'pbb-review-item__patch-pair';
        patchPair.setAttribute('aria-hidden', 'true');
        ['upper', 'lower'].forEach((position) => {
          const patchPreview = document.createElement('span');
          patchPreview.className = `pbb-review-item__patch-preview pbb-review-item__patch-preview--${position}`;
          patchPreview.dataset.pbbReviewPatchName = '';
          patchPreview.textContent = this.state.dogName;
          patchPair.append(patchPreview);
        });
        media.append(patchPair);
      } else if (selection.image) {
        const image = document.createElement('img');
        image.alt = '';
        image.loading = 'eager';
        image.decoding = 'async';
        image.addEventListener('load', () => this.updateNamePreviews());
        image.addEventListener('error', () => this.updateNamePreviews());
        image.src = selection.image;
        media.append(image);
      } else {
        const placeholder = document.createElement('div');
        placeholder.className = 'pbb-review-item__placeholder';
        if (selection.step === 'name_patch') {
          placeholder.classList.add('pbb-review-item__placeholder--patch');
          placeholder.textContent = 'Custom patch';
        }
        media.append(placeholder);
      }

      if (selection.step === 'harness' && selection.nameProfile && selection.image) {
        media.dataset.pbbNameProfile = selection.nameProfile;
        if (selection.nameTone) {
          media.dataset.pbbNameTone = selection.nameTone;
        }
        if (selection.nameImageSignature) {
          media.dataset.pbbNameImageSignature = selection.nameImageSignature;
        }
        const namePreview = document.createElement('span');
        namePreview.className = 'pbb-name-preview';
        namePreview.dataset.pbbNamePreview = '';
        namePreview.setAttribute('aria-hidden', 'true');
        namePreview.hidden = true;
        media.append(namePreview);
      }
      item.append(media);

      const body = document.createElement('div');
      body.className = 'pbb-review-item__body';
      const step = document.createElement('small');
      step.textContent = selection.step.replaceAll('_', ' ');
      const title = document.createElement('strong');
      title.textContent = selection.title;
      const variant = document.createElement('span');
      variant.textContent = displayVariantTitle(selection.variantTitle);
      body.append(step, title, variant);
      if (selection.step === 'name_patch') {
        const personalisation = document.createElement('span');
        personalisation.className = 'pbb-review-item__personalisation';
        personalisation.textContent = `Patch text: ${this.state.dogName}`;
        body.append(personalisation);
      }
      item.append(body);

      const change = document.createElement('button');
      change.type = 'button';
      change.className = 'pbb-review-item__change';
      change.dataset.pbbChangeStep = PRODUCT_STEPS.includes(selection.step) ? selection.step : 'harness';
      change.textContent = 'Change';
      const changeLabel = PRODUCT_STEPS.includes(selection.step)
        ? selection.step
        : 'harness and included components';
      change.setAttribute('aria-label', `Change ${changeLabel}`);
      item.append(change);
      return item;
    }

    reviewMissingItem(step) {
      const item = document.createElement('article');
      item.className = 'pbb-review-item pbb-review-item--missing';

      const media = document.createElement('div');
      media.className = 'pbb-review-item__media';
      const placeholder = document.createElement('div');
      placeholder.className = 'pbb-review-item__placeholder';
      placeholder.textContent = '!';
      media.append(placeholder);

      const body = document.createElement('div');
      body.className = 'pbb-review-item__body';
      const label = document.createElement('small');
      label.textContent = step.replaceAll('_', ' ');
      const title = document.createElement('strong');
      title.textContent = 'Needs a selection';
      body.append(label, title);

      const change = document.createElement('button');
      change.type = 'button';
      change.className = 'pbb-review-item__change';
      change.dataset.pbbChangeStep = PRODUCT_STEPS.includes(step) ? step : 'harness';
      change.textContent = 'Choose';
      const targetLabel = PRODUCT_STEPS.includes(step) ? step : 'harness and included components';
      change.setAttribute('aria-label', `Choose ${targetLabel}`);
      item.append(media, body, change);
      return item;
    }

    allSelectionsReady() {
      const harness = this.state.selections.harness;
      return Boolean(this.state.dogName) &&
        PRODUCT_STEPS.every((step) => this.state.selections[step]?.variantId) &&
        Boolean(this.companionSelection(harness?.patchProduct, 'name_patch')) &&
        Boolean(this.companionSelection(this.dRingProduct, 'd_ring'));
    }

    lineProperties(step, bundleId) {
      return {
        '_atc-sesh': bundleId,
        '_customBundle': bundleId,
        '_dfc_bundle_group': bundleId,
        '_dfc_personalised_bundle': 'v1',
        '_dfc_bundle_step': step,
        '_dfc_bundle_offer': BUNDLE_OFFER,
        '_dfc_dog_name': this.state.dogName,
      };
    }

    async fetchCart() {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(this.config.cartJsonUrl || `${root}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await response.json();
      if (!response.ok) throw new Error('The cart could not be checked after adding the bundle.');
      return cart;
    }

    async syncNamePatchAttributes(cart, sections, attributes = reconcileNamePatchAttributes(cart.items, cart.attributes)) {
      let lastError = null;

      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(this.config.cartUpdateUrl || `${root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes, sections, sections_url: window.location.pathname }),
          });
          const updatedCart = await response.json();
          if (!response.ok) {
            throw new Error(updatedCart.description || updatedCart.message || 'The name patch could not be attached.');
          }
          return updatedCart;
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError || new Error('The name patch could not be attached.');
    }

    async removeBundleGroup(cart, bundleId, sections, extraAttributeKeys = []) {
      const lines = groupLines(cart, bundleId);
      const updates = Object.fromEntries(lines.filter((line) => line.key).map((line) => [line.key, 0]));
      if (!Object.keys(updates).length && !extraAttributeKeys.length) return cart;

      const remainingItems = (cart.items || []).filter((item) => itemGroup(item) !== String(bundleId));
      const attributes = reconcileNamePatchAttributes(remainingItems, cart.attributes);
      extraAttributeKeys.forEach((key) => {
        if (!attributes[key]) attributes[key] = '';
      });
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(this.config.cartUpdateUrl || `${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, attributes, sections, sections_url: window.location.pathname }),
      });
      const updatedCart = await response.json();
      if (!response.ok) throw new Error(updatedCart.description || updatedCart.message || 'The invalid pack could not be removed.');
      const groupWasRemoved = groupLines(updatedCart, bundleId).length === 0;
      const attemptedKeysWereCleared = extraAttributeKeys.every(
        (key) => String(updatedCart.attributes?.[key] || '') === String(attributes[key] || ''),
      );
      if (!groupWasRemoved || !attemptedKeysWereCleared) {
        throw new Error('The invalid pack cleanup could not be confirmed.');
      }
      return updatedCart;
    }

    async removeOtherPersonalisedBundleGroups(cart, bundleId, sections) {
      const lines = otherPersonalisedBundleLines(cart, bundleId);
      if (!lines.length) return cart;
      if (lines.some((line) => !line.key)) {
        throw new Error('The previous personalized pack could not be replaced. Open the cart and remove it before trying again.');
      }

      const removalKeys = new Set(lines.map((line) => line.key));
      const updates = Object.fromEntries(lines.map((line) => [line.key, 0]));
      const remainingItems = (cart.items || []).filter((item) => !removalKeys.has(item.key));
      const attributes = reconcileNamePatchAttributes(remainingItems, cart.attributes);
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(this.config.cartUpdateUrl || (root + 'cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, attributes, sections, sections_url: window.location.pathname }),
      });
      const updatedCart = await response.json();
      if (!response.ok) {
        throw new Error(updatedCart.description || updatedCart.message || 'The previous personalized pack could not be replaced.');
      }
      if (
        otherPersonalisedBundleLines(updatedCart, bundleId).length ||
        !namePatchAttributesConfirmed(updatedCart)
      ) {
        throw new Error('The previous personalized pack cleanup could not be confirmed.');
      }
      return updatedCart;
    }

    removedBundleError(message) {
      const error = new Error(message);
      error.bundleState = 'removed';
      return error;
    }

    async finalizeBundleCart(cart, bundleId, sections) {
      const quote = this.presentmentQuote;
      const verification = quote?.ok
        ? verifyBundleCart(
          cart,
          bundleId,
          quote.expectedCartAmount,
          true,
          quote.currencyCode,
          quote.cartTolerance,
        )
        : { ok: false, message: quote?.message || 'The local bundle price could not be verified.' };
      if (!verification.ok) {
        await this.removeBundleGroup(cart, bundleId, sections);
        throw this.removedBundleError(verification.message);
      }

      const pendingAttributes = reconcileNamePatchAttributes(cart.items, cart.attributes);
      const newlyAttemptedAttributeKeys = Object.keys(pendingAttributes).filter(
        (key) => !Object.prototype.hasOwnProperty.call(cart.attributes || {}, key),
      );
      let updatedCart;
      try {
        updatedCart = await this.syncNamePatchAttributes(cart, sections, pendingAttributes);
      } catch (syncError) {
        let removalCart = cart;
        try {
          removalCart = await this.fetchCart();
        } catch (refreshError) {
          console.error('The cart could not be refreshed before rollback.', refreshError);
        }
        await this.removeBundleGroup(removalCart, bundleId, sections, newlyAttemptedAttributeKeys);
        throw this.removedBundleError(`${this.copy('personalizationNoun', 'Personalization')} could not be confirmed, so the pack was removed. Please try again.`);
      }

      const finalVerification = verifyBundleCart(
        updatedCart,
        bundleId,
        quote.expectedCartAmount,
        true,
        quote.currencyCode,
        quote.cartTolerance,
      );
      if (!finalVerification.ok || !namePatchAttributesConfirmed(updatedCart)) {
        await this.removeBundleGroup(updatedCart, bundleId, sections, newlyAttemptedAttributeKeys);
        throw this.removedBundleError(
          finalVerification.ok
            ? `${this.copy('personalizationNoun', 'Personalization')} could not be confirmed, so the pack was removed. Please try again.`
            : finalVerification.message,
        );
      }

      return updatedCart;
    }

    proceedToCheckout() {
      if (this.checkoutStarted) return;
      const form = this.querySelector('[data-pbb-checkout-form]');
      const submitter = form?.querySelector('[data-pbb-checkout-submit]');
      if (!form || !submitter) {
        window.location.assign(this.config.cartUrl || '/cart');
        return;
      }

      this.checkoutStarted = true;
      this.checkoutFallbackReady = false;
      const checkoutStartUrl = window.location.href;
      const activateCheckoutFallback = () => {
        if (!this.checkoutStarted) return;
        if (document.visibilityState !== 'visible' || window.location.href !== checkoutStartUrl) return;
        this.checkoutStarted = false;
        this.checkoutFallbackReady = true;
        const checkoutButton = this.querySelector('[data-pbb-add-bundle]');
        if (checkoutButton) {
          checkoutButton.disabled = false;
          checkoutButton.removeAttribute('aria-busy');
          checkoutButton.textContent = 'Open cart to checkout';
        }
        this.lockCommittedReview();
        this.showCartError('Checkout did not open. Your verified pack is in the cart; open the cart to continue.');
      };
      try {
        form.addEventListener('submit', (event) => {
          window.setTimeout(() => {
            if (!event.defaultPrevented) return;
            activateCheckoutFallback();
          }, 1000);
        }, { once: true });
        const insuranceOverlay = submitter
          .closest('.sam__button-overlay-wrapper')
          ?.querySelector('.sam__checkout-btn-overlay');
        if (insuranceOverlay && window.getComputedStyle(insuranceOverlay).display !== 'none') {
          insuranceOverlay.click();
        } else {
          submitter.click();
        }
        window.setTimeout(activateCheckoutFallback, 8000);
      } catch (checkoutError) {
        this.checkoutStarted = false;
        console.error('Checkout could not be started.', checkoutError);
        window.location.assign(this.config.cartUrl || '/cart');
      }
    }

    lockCommittedReview() {
      this.stepLinks.forEach((button) => {
        button.disabled = true;
      });
      this.querySelectorAll('[data-pbb-change-step]').forEach((button) => {
        button.disabled = true;
        button.title = 'This verified pack is already in the cart';
      });
      const backButton = this.querySelector('[data-pbb-back]');
      if (backButton) backButton.hidden = true;
    }

    async addBundleToCart() {
      const button = this.querySelector('[data-pbb-add-bundle]');
      const errorElement = this.querySelector('[data-pbb-cart-error]');
      if (!button) return;
      this.updatePresentmentPricing();
      if (button.disabled) return;

      if (!this.allSelectionsReady()) {
        this.showCartError('Please complete every bundle step before adding the pack.');
        return;
      }

      const patchProduct = this.state.selections.harness?.patchProduct;
      const patch = this.companionSelection(patchProduct, 'name_patch');
      const dRing = this.companionSelection(this.dRingProduct, 'd_ring');
      if (!patch?.variantId || !dRing?.variantId) {
        this.showCartError('This harness does not have an available name patch and D-ring for the selected size. Please choose another harness or size.');
        return;
      }

      if (errorElement) errorElement.hidden = true;
      const previousText = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Preparing checkout…';

      const harness = this.state.selections.harness;
      const bundleId = createBundleId(harness.variantId);
      const items = PRODUCT_STEPS.map((step) => {
        const selection = this.state.selections[step];
        return {
          id: selection.variantId,
          quantity: 1,
          properties: this.lineProperties(step, bundleId),
        };
      });

      items.splice(1, 0, {
        id: patch.variantId,
        quantity: 1,
        properties: {
          ...this.lineProperties('name_patch', bundleId),
          'Name Patch Text': this.state.dogName,
          '_dfc_patch_included_with_mapped_bundle': 'true',
          '_dfc_mapped_bundle_parent_variant_id': String(harness.variantId),
          '_dfc_mapped_bundle_component_variant_id': String(patch.variantId),
        },
      });

      items.splice(2, 0, {
        id: dRing.variantId,
        quantity: 1,
        properties: {
          ...this.lineProperties('d_ring', bundleId),
          '_dfc_bundle_component': 'd_ring',
          '_dfc_mapped_bundle_parent_variant_id': String(harness.variantId),
          '_dfc_mapped_bundle_component_variant_id': String(dRing.variantId),
        },
      });

      const sections = ['cart-drawer', 'cart-icon-bubble'];
      let addState = 'not-sent';
      let finalizationComplete = false;

      try {
        const initialCart = await this.fetchCart();
        if (String(initialCart.currency || '').toUpperCase() !== this.presentmentQuote.currencyCode) {
          throw new Error(
            'Your cart currency changed from ' + this.presentmentQuote.currencyCode + '. Refresh the page before checkout.',
          );
        }

        const root = window.Shopify?.routes?.root || '/';
        addState = 'unknown';
        const addResponse = await fetch(this.config.cartAddUrl || `${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
        });
        if (!addResponse.ok) {
          addState = addResponse.status >= 400 && addResponse.status < 500 ? 'rejected' : 'unknown';
          const addJson = await addResponse.json().catch(() => ({}));
          throw new Error(addJson.description || addJson.message || 'The bundle could not be added.');
        }
        addState = 'committed';

        const addedCart = await this.fetchCart();
        const replacementCart = await this.removeOtherPersonalisedBundleGroups(addedCart, bundleId, sections);
        const updatedCart = await this.finalizeBundleCart(replacementCart, bundleId, sections);
        finalizationComplete = true;

        button.removeAttribute('aria-busy');
        button.textContent = 'Taking you to checkout…';
        this.announce(`${this.state.dogName}'s ${this.copy('personalizedPack', 'personalized pack')} is ready. Taking you to checkout.`);

        this.proceedToCheckout();
      } catch (error) {
        console.error('Personalised bundle add-to-cart failed.', error);
        let failure = error;
        if (failure.bundleState) addState = failure.bundleState;
        let recoveredUpdatedCart = null;
        if (addState === 'unknown' || (addState === 'committed' && !finalizationComplete)) {
          try {
            let recoveryCart = await this.fetchCart();
            if (groupLines(recoveryCart, bundleId).length) {
              addState = 'committed';
              recoveryCart = await this.removeOtherPersonalisedBundleGroups(recoveryCart, bundleId, sections);
              recoveredUpdatedCart = await this.finalizeBundleCart(recoveryCart, bundleId, sections);
              finalizationComplete = true;
            }
          } catch (recoveryError) {
            failure = recoveryError;
            if (failure.bundleState) addState = failure.bundleState;
            console.error('Personalised bundle recovery did not finish.', recoveryError);
          }
        }

        button.removeAttribute('aria-busy');
        if (recoveredUpdatedCart) {
          button.textContent = 'Taking you to checkout…';
          this.announce(`${this.state.dogName}'s ${this.copy('personalizedPack', 'personalized pack')} is ready. Taking you to checkout.`);
          this.proceedToCheckout();
          return;
        }

        if (addState === 'committed') {
          this.showCartError('The pack was added, but final confirmation did not finish. Please review your cart before trying again.');
          button.textContent = 'Pack added — review cart';
          window.location.assign(this.config.cartUrl || '/cart');
        } else if (addState === 'unknown') {
          this.showCartError('The add status could not be confirmed. Please review your cart before trying again.');
          button.textContent = 'Review cart before retrying';
          window.location.assign(this.config.cartUrl || '/cart');
        } else {
          this.showCartError(failure.message || 'The pack could not be added. Please try again.');
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = previousText;
        }
      }
    }

    showCartError(message) {
      const element = this.querySelector('[data-pbb-cart-error]');
      if (!element) return;
      element.textContent = message;
      element.hidden = false;
      this.announce(message);
    }

    announce(message) {
      const region = this.querySelector('[data-pbb-live-region]');
      if (region) region.textContent = message;
    }
  }

  if (typeof customElements !== 'undefined' && !customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, PersonalisedBundleBuilder);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      companionRangeForHarness,
      companionSizeScore,
      createPresentmentDisplayPrice,
      createPresentmentQuote,
      createBundleId,
      currencyFractionDigits,
      exactCompanionVariant,
      groupLines,
      harnessSizeToken,
      isPersonalisedBundleLine,
      namePatchAttributesConfirmed,
      normaliseSize,
      otherPersonalisedBundleLines,
      reconcileNamePatchAttributes,
      reviewImageUrl,
      verifyBundleCart,
    };
  }
})();
